import { createPrototypeRuntimeContext, offlinePrototypeRuntime } from './prototype'

/**
 * App-level active-runtime switch.
 *
 * Foundation's `prototype.ts` owns the runtime factory
 * (`createPrototypeRuntimeContext`) and the offline singleton
 * (`offlinePrototypeRuntime`). This app owns *which* context is live: it starts
 * offline (seeded dummy stores) and swaps to a live, auth-scoped context once a
 * JWT is available (see `ProjectionRuntimeBridge` in `ChooseChowApp`).
 *
 * This lives outside `prototype.ts` on purpose: that file is a `create`-mode
 * Foundation scaffold and stays byte-identical to the template so
 * `make foundation-update` seed-drift detection keeps working. Project-owned
 * composition belongs here.
 */
export type PrototypeRuntimeContext = ReturnType<typeof createPrototypeRuntimeContext>

// A live build (every deploy) carries no client-side placeholder rows: the
// pre-auth offline runtime is empty, so nothing renders until a real session
// binds live projections. Dev keeps the foundation's seeded offline singleton
// so layouts preview before a backend exists. Mirrors `allowSeedContent()` in
// lib/runtime, inlined here to avoid a module-init import cycle.
const allowSeedContent =
  !import.meta.env.PROD || import.meta.env.VITE_ALLOW_DEMO_CONTENT === 'true'

const offlineRuntime: PrototypeRuntimeContext = allowSeedContent
  ? offlinePrototypeRuntime
  : createPrototypeRuntimeContext({ live: false, dummyRecordCount: 0 })

let activePrototypeRuntime: PrototypeRuntimeContext = offlineRuntime
const runtimeListeners = new Set<() => void>()

export const getActivePrototypeRuntime = (): PrototypeRuntimeContext => activePrototypeRuntime

export const setActivePrototypeRuntime = (runtime: PrototypeRuntimeContext): void => {
  if (activePrototypeRuntime === runtime) return
  activePrototypeRuntime = runtime
  runtimeListeners.forEach((listener) => listener())
}

export const resetActivePrototypeRuntime = (): void => {
  activePrototypeRuntime.liveBindings.forEach((binding) => binding.disconnect())
  setActivePrototypeRuntime(offlineRuntime)
}

export const subscribePrototypeRuntime = (listener: () => void): (() => void) => {
  runtimeListeners.add(listener)
  return () => runtimeListeners.delete(listener)
}

/**
 * Whether a live runtime is about to replace the offline one.
 *
 * The bridge swaps runtimes in an effect, so a signed-in session's first render
 * reads the offline runtime: empty in a deploy, seeded dummy rows in dev. A
 * screen that trusted that frame said "no conversations yet" (or drew
 * prototype rows) before the real snapshot was even requested. The bridge
 * declares its intent while rendering — before any child reads a store — so
 * readers can tell "not connected yet" from "connected, and empty".
 */
let liveRuntimeExpected = false

export const expectLiveRuntime = (expected: boolean): void => {
  liveRuntimeExpected = expected
}

export const isLiveRuntimePending = (): boolean =>
  liveRuntimeExpected && activePrototypeRuntime.mode !== 'live'
